import React, { useRef, useState, useEffect, useId } from "react";
import { createPortal } from "react-dom";

export interface U2000ActionDropdownItem {
  key: string;
  label: React.ReactNode;
  handleClick: () => void;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  disabled?: boolean;
  /** Infobulle explicite ; sinon libellé string utilisé si le texte est tronqué à l’affichage. */
  title?: string;
}

/** Alignement horizontal du menu par rapport au bouton. */
export type U2000ActionDropdownMenuAlign = "left" | "right";

type U2000ActionDropdownBtnProps = {
  key?: string;
  label: React.ReactNode;
  hasRole?: boolean;
  disabled?: boolean;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  items: U2000ActionDropdownItem[];
  /** Alignement du menu : "left" (bord gauche du menu = bord gauche du bouton) ou "right" (bord droit = bord droit). Défaut : "left". */
  menuAlign?: U2000ActionDropdownMenuAlign;
  handleClick?: () => void;
  className?: string;
};

const MENU_ITEM_HEIGHT_ESTIMATE = 44;
const MENU_PADDING_Y = 8;
/** Délai avant affichage infobulle libellé tronqué (évite le ~1 s du `title` natif). */
const TRUNCATED_TOOLTIP_SHOW_MS = 250;
const TRUNCATED_TOOLTIP_Z_INDEX = 1001;

function closeAndFocusTrigger(triggerRef: React.RefObject<HTMLButtonElement | null>, setIsOpen: (v: boolean) => void) {
  setIsOpen(false);
  requestAnimationFrame(() => triggerRef.current?.focus());
}

function u2000DropdownItemTooltipText(label: React.ReactNode, title?: string): string | undefined {
  if (title) return title;
  return typeof label === "string" ? label : undefined;
}

/** Libellé menu : infobulle rapide si le texte est ellipsé (truncate). */
const U2000DropdownMenuItemLabel: React.FC<{ label: React.ReactNode; title?: string }> = ({ label, title }) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);
  const tooltipSource = u2000DropdownItemTooltipText(label, title);

  useEffect(() => {
    const el = spanRef.current;
    if (!el || !tooltipSource) {
      setIsTruncated(false);
      return;
    }
    const sync = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, [label, tooltipSource]);

  useEffect(
    () => () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
      }
    },
    [],
  );

  const hideTooltip = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    setTooltipPos(null);
  };

  const handleMouseEnter = () => {
    if (!isTruncated || !tooltipSource) return;
    hideTooltip();
    showTimerRef.current = setTimeout(() => {
      const rect = spanRef.current?.getBoundingClientRect();
      if (!rect) return;
      setTooltipPos({ top: rect.bottom + 4, left: rect.left });
    }, TRUNCATED_TOOLTIP_SHOW_MS);
  };

  const tooltipNode =
    tooltipPos && tooltipSource
      ? createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed max-w-sm rounded-md bg-gray-900 px-2.5 py-1.5 text-left text-xs leading-snug text-white shadow-lg"
            style={{
              top: tooltipPos.top,
              left: tooltipPos.left,
              zIndex: TRUNCATED_TOOLTIP_Z_INDEX,
            }}
          >
            {tooltipSource}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <span
        ref={spanRef}
        className="min-w-0 flex-1 truncate"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={hideTooltip}
        onFocus={handleMouseEnter}
        onBlur={hideTooltip}
      >
        {label}
      </span>
      {tooltipNode}
    </>
  );
};

const U2000ActionDropdownBtn: React.FC<U2000ActionDropdownBtnProps> = ({
  label,
  hasRole,
  disabled,
  icon: Icon,
  items,
  menuAlign = "left",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId().replace(/:/g, "");
  const generatedMenuId = `u2000-dropdown-menu-${menuId}`;

  const effectiveDisabled = disabled || !items?.length;
  const visibleItems = items ?? [];

  // Clic à l'extérieur : fermer et remettre le focus. Utiliser "click" (et non "mousedown")
  // pour éviter que Safari ferme le menu avant le click sur l'item (ordre événements différent).
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!target.closest("[data-u2000-dropdown-menu]")) {
          closeAndFocusTrigger(triggerRef, setIsOpen);
        }
      }
    };
    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [isOpen]);

  // Position du menu (sous le bouton ou au-dessus si pas de place) + correction bords viewport
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const menuWidth = Math.max(rect.width, 200);
      const menuHeightEstimate = visibleItems.length * MENU_ITEM_HEIGHT_ESTIMATE + MENU_PADDING_Y * 2;
      const gap = 4;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const openBelow = spaceBelow >= menuHeightEstimate || spaceBelow >= spaceAbove;
      let top: number;
      if (openBelow) {
        top = rect.bottom + gap;
      } else {
        top = rect.top - menuHeightEstimate - gap;
      }
      let left =
        menuAlign === "right"
          ? rect.right - menuWidth
          : rect.left;
      if (left < 8) left = 8;
      if (left + menuWidth > window.innerWidth - 8) left = window.innerWidth - menuWidth - 8;
      setMenuRect({ left, top, width: menuWidth });
    } else {
      setMenuRect(null);
    }
  }, [isOpen, visibleItems.length, menuAlign]);

  // À l’ouverture : focus sur le premier item non désactivé (menuRect garantit que le menu est rendu)
  useEffect(() => {
    if (!isOpen || !menuRect || !menuRef.current) return;
    const firstItem = menuRef.current.querySelector<HTMLButtonElement>(
      '[role="menuitem"]:not(:disabled)'
    );
    requestAnimationFrame(() => firstItem?.focus());
  }, [isOpen, menuRect]);

  // Clavier : Escape ferme ; flèches naviguent entre items (menuRect garantit que le menu est rendu)
  useEffect(() => {
    if (!isOpen || !menuRect || !menuRef.current) return;
    const menu = menuRef.current;
    const items = menu.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)');
    const count = items.length;
    if (count === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (!menu.contains(target)) return;
      if (target.getAttribute("role") !== "menuitem") return;

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          closeAndFocusTrigger(triggerRef, setIsOpen);
          return;
        case "ArrowDown":
          e.preventDefault();
          {
            const idx = Array.from(items).indexOf(target as HTMLButtonElement);
            const next = idx < count - 1 ? items[idx + 1] : items[0];
            next?.focus();
          }
          return;
        case "ArrowUp":
          e.preventDefault();
          {
            const idx = Array.from(items).indexOf(target as HTMLButtonElement);
            const prev = idx > 0 ? items[idx - 1] : items[count - 1];
            prev?.focus();
          }
          return;
        case "Home":
          e.preventDefault();
          items[0]?.focus();
          return;
        case "End":
          e.preventDefault();
          items[count - 1]?.focus();
          return;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, menuRect, visibleItems.length]);

  // Focus quitte le menu (ex. Tab) : fermer et focus sur le trigger si on ne va pas sur le trigger
  useEffect(() => {
    if (!isOpen || !menuRect || !menuRef.current) return;
    const menu = menuRef.current;
    const handleFocusOut = (e: FocusEvent) => {
      const relatedTarget = e.relatedTarget as Node | null;
      if (relatedTarget && (menu.contains(relatedTarget) || triggerRef.current === relatedTarget))
        return;
      closeAndFocusTrigger(triggerRef, setIsOpen);
    };
    menu.addEventListener("focusout", handleFocusOut);
    return () => menu.removeEventListener("focusout", handleFocusOut);
  }, [isOpen, menuRect]);

  if (hasRole !== undefined && hasRole === false) {
    return null;
  }

  const menuContent =
    isOpen && visibleItems.length > 0 && menuRect ? (
      <div
        ref={menuRef}
        id={generatedMenuId}
        data-u2000-dropdown-menu
        role="menu"
        aria-label={typeof label === "string" ? label : undefined}
        className="fixed z-1000 min-w-[200px] rounded-lg border border-blue-700/30 bg-blue-600 py-1 shadow-lg transition-all duration-150 motion-safe:animate-slide-up motion-reduce:animate-none motion-reduce:opacity-100"
        style={{
          left: menuRect.left,
          top: menuRect.top,
          width: menuRect.width,
        }}
      >
        {visibleItems.map((item) => {
          const ItemIcon = item.icon;
          const runAction = () => {
            if (!item.disabled) {
              item.handleClick();
              closeAndFocusTrigger(triggerRef, setIsOpen);
            }
          };
          return (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-3 rounded-md bg-blue-600 px-3 py-2.5 text-left text-sm text-white transition-colors duration-150 hover:bg-blue-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-1 focus-visible:ring-offset-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                runAction();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  runAction();
                }
              }}
              onClick={(e) => e.preventDefault()}
              disabled={item.disabled}
            >
              {ItemIcon && <ItemIcon className="h-4 w-4 shrink-0 text-blue-100" />}
              <U2000DropdownMenuItemLabel label={item.label} title={item.title} />
            </button>
          );
        })}
      </div>
    ) : null;

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        className={`inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-xs transition-all duration-200 
          hover:bg-blue-700 hover:shadow-md 
          focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 
          active:bg-blue-800 active:scale-95 
          disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-200 disabled:opacity-60 disabled:shadow-none 
          ${className}`}
        onClick={() => !effectiveDisabled && setIsOpen((v) => !v)}
        disabled={effectiveDisabled}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls={isOpen ? generatedMenuId : undefined}
      >
        {Icon && <Icon className="mr-2 h-4 w-4 shrink-0" />}
        <span>{label}</span>
        <svg className="ml-2 h-4 w-4 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {menuContent && createPortal(menuContent, document.body)}
    </div>
  );
};

U2000ActionDropdownBtn.displayName = "U2000ActionDropdownBtn";

export default U2000ActionDropdownBtn;
